import React, { useState, useEffect, Suspense } from "react";
import { HashRouter, Route, Switch, Redirect } from "react-router-dom";
import "./scss/style.scss";
import { useSelector, useDispatch } from "react-redux";
import { selectUser, login, logout } from "./redux_setup/slices/userSlice";
import {
  fetchSettingsStart,
  fetchSettingsSuccess,
} from "./redux_setup/slices/settingSlice";
import { selectSettings } from "src/redux_setup/slices/settingSlice";
import { selectSidebarShow, setSidebarShow } from "src/redux_setup/slices/sidebarSlice";
import LoadingComponent from "./views/components/ui/LoadingComponent";

const TheLayout = React.lazy(() => import("./containers/TheLayout"));
const Login = React.lazy(() => import("./views/pages/login/Login"));
const Forgot = React.lazy(() => import("./views/pages/forgot"));
const Page404 = React.lazy(() => import("./views/pages/page404/Page404"));
const Page500 = React.lazy(() => import("./views/pages/page500/Page500"));
const Verify = React.lazy(() => import("./views/pages/login/Verify"));

const App = () => {
  const dispatch = useDispatch();
  const [isInitializing, setIsInitializing] = useState(true);
  const user = useSelector(selectUser);
  const settings = useSelector(selectSettings);
  const sidebarShow = useSelector(selectSidebarShow);
  const [primaryColor, setPrimaryColor] = useState("#3498db");
  const [settingsRetryCount, setSettingsRetryCount] = useState(0);
  const maxRetries = 3;

  const defaultSettings = {
    siteTitle: "Default Hospital System",
    description: "Default description",
    keywords: "default, hospital",
    favicon: "",
    primaryColor: "#3498db",
    primaryTextColor: "#ffffff",
    secondaryColor: "#f1f1f1",
    secondaryTextColor: "#000000",
    sidebarColor: "#2c3e50",
    bodyColor: "#ffffff",
  };

  useEffect(() => {
    const initializeApp = async () => {
      const tempToken = localStorage.getItem("temp_token");
      dispatch(setSidebarShow(window.innerWidth >= 768));

      if (tempToken && tempToken !== "null") {
        try {
          const url = `${process.env.REACT_APP_MAIN_URL}/api/users/verify`;
          const response = await fetch(url, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${tempToken}`,
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ emptydata: "" }),
          });
          const res = await response.json();

          if (res?.success === 1) {
            const user_name = localStorage.getItem("temp_user_name");
            const user_role = localStorage.getItem("temp_user_role");
            const u_login = localStorage.getItem("temp_login");
            const u_photo = localStorage.getItem("temp_photo");
            const u_special_permissions = JSON.parse(localStorage.getItem("temp_special_permissions") || '[]');
            dispatch(
              login({
                userName: user_name,
                role: user_role,
                token: tempToken,
                staffId: u_login,
                photo: u_photo,
                special_permissions: u_special_permissions,
              })
            );
          } else {
            dispatch(logout());
            clearLocalStorage();
          }
        } catch (err) {
          if (!user) {
            dispatch(logout());
            clearLocalStorage();
          }
        }
      } else if (!user) {
        dispatch(logout());
      }

      setIsInitializing(false);
    };

    initializeApp();
  }, [dispatch]);

  useEffect(() => {
    const fetchSettingsWithRetry = async (token) => {
      dispatch(fetchSettingsStart());
      try {
        const response = await fetch(`${process.env.REACT_APP_MAIN_URL}/api/settings/`, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        });
        const settingsData = await response.json();

        if (settingsData?.success === 1) {
          dispatch(fetchSettingsSuccess(settingsData.data));
          setPrimaryColor(settingsData.data.primaryColor);
          document.documentElement.style.setProperty("--primary-color", settingsData.data.primaryColor);
          document.documentElement.style.setProperty("--primary-text-color", settingsData.data.primaryTextColor);
          document.documentElement.style.setProperty("--secondary-color", settingsData.data.secondaryColor);
          document.documentElement.style.setProperty("--seondary-text-color", settingsData.data.secondaryTextColor);
          document.documentElement.style.setProperty("--sidebar-color", settingsData.data.sidebarColor);
          // document.documentElement.style.setProperty("--body-color", '#f9fafc'); //settingsData.data.bodyColor
        } else {
          throw new Error("Failed to fetch settings");
        }
      } catch (error) {
        if (settingsRetryCount < maxRetries) {
          setTimeout(() => {
            setSettingsRetryCount(settingsRetryCount + 1);
          }, 2000);
        } else {
          dispatch(fetchSettingsSuccess(defaultSettings));
        }
      }
    };

    if (!isInitializing) {
      fetchSettingsWithRetry(user?.token);
    }
  }, [dispatch, isInitializing, user, settingsRetryCount]);

  useEffect(() => { 
    const handleKeydown = (e) => {
      if (typeof e.key === "string") {
        console.log("Key pressed:", e.key);
      }
    };

    document.addEventListener("keydown", handleKeydown);

    return () => {
      document.removeEventListener("keydown", handleKeydown);
    };
  }, []);

  useEffect(() => {
    if (settings && Object.keys(settings).length > 0) {
      document.title = settings.siteTitle || document.title;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute("content", settings.description || metaDesc.content);
      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) metaKeywords.setAttribute("content", settings.keywords || metaKeywords.content);
      const favicon = document.querySelector('link[rel="icon"]');
      if (favicon) favicon.href = settings.favicon ? `${process.env.REACT_APP_MAIN_URL}/uploads/${settings.favicon}` : favicon.href;
    }
  }, [settings]);

  useEffect(() => {
    const handleKeydown = (e) => {
      const key = e.key || '';
      if (key === 'Escape' && sidebarShow) {
        dispatch(setSidebarShow(false));
        e.stopPropagation();
      }
    };
    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [sidebarShow, dispatch]);

  const clearLocalStorage = () => {
    localStorage.setItem("temp_token", null);
    localStorage.setItem("temp_user_name", null);
    localStorage.setItem("temp_user_role", null);
    localStorage.setItem("temp_login", null);
    localStorage.setItem("temp_photo", null);
    localStorage.setItem("temp_special_permissions", null);
    localStorage.setItem("temp_permissions", []);
  };

  if (isInitializing) {
    return <LoadingComponent isActive={true} />;
  }

  return (
    <HashRouter>
      <Suspense fallback={<LoadingComponent isActive={true} />}>
        <Switch>
          {!user ? (
            <>
              <Route exact path="/login" render={(props) => <Login {...props} />} />
              <Route exact path="/forgot-password" render={(props) => <Forgot {...props} />} />
              <Route exact path="/verify" render={(props) => <Verify {...props} />} />
              <Route path="/" render={() => <Redirect to="/login" />} />
            </>
          ) : (
            <>
              <Route exact path="/404" render={(props) => <Page404 {...props} />} />
              <Route exact path="/500" render={(props) => <Page500 {...props} />} />
              <Route path="/" render={(props) => <TheLayout {...props} />} />
            </>
          )}
        </Switch>
      </Suspense>
    </HashRouter>
  );
};

export default App;